import moment from "moment";

const partLabel = (part) => (part ? "Part " + part : "");

const partListLabel = (parts = []) => {
  if (parts.length === 0) return "";
  if (parts.length === 1) return partLabel(parts[0]);
  return "Parts " + parts.slice(0, -1).join(", ") + " and " + parts[parts.length - 1];
};

// Part change entries are written by the backend with structured details; see PackPartHistoryWriter.
const partChangeEvents = {
  due: {
    type: "part-due",
    title: "Upcoming part change due",
    facts: (d) => [
      ["Current part", partLabel(d.from_part)],
      ["Upcoming part", partLabel(d.to_part)],
      ["Scheduled change", d.scheduled_datetime ? moment(d.scheduled_datetime).format("D MMM YYYY, HH:mm") : ""],
      ["Planned changes", d.summary],
    ],
  },
  acknowledged: {
    type: "part-acknowledged",
    title: "Upcoming part change acknowledged",
    actorLabel: "Acknowledged by",
    facts: (d) => [
      ["Current part", partLabel(d.from_part)],
      ["Upcoming part", partLabel(d.to_part)],
    ],
  },
  confirmed: {
    type: "part-confirmed",
    title: "Part change confirmed as implemented",
    actorLabel: "Confirmed by",
    facts: (d) => [
      ["Previous part", partLabel(d.from_part)],
      ["New part", partLabel(d.to_part)],
      ["ES comments", d.comments],
    ],
  },
  activated: {
    type: "part-activated",
    title: "Worksite part activated",
    facts: (d) => [
      ["Previous part", partLabel(d.from_part)],
      ["Activated part", partLabel(d.to_part)],
      ["Bypassed parts", partListLabel(d.bypassed_parts)],
      ["Reason", d.reason],
    ],
  },
  completed: {
    type: "part-completed",
    title: "Final worksite part completed",
    facts: (d) => [
      ["Completed part", partLabel(d.from_part)],
      ["Bypassed parts", partListLabel(d.bypassed_parts)],
      ["Reason", d.reason],
    ],
  },
};

const typeForAction = (action) => {
  const lowerAction = action.toLowerCase();
  return lowerAction.includes("export") ? "exported"
    : lowerAction.includes("void") ? "voided"
    : lowerAction.includes("reject") ? "rejected"
    : lowerAction.includes("return") ? "returned"
    : lowerAction.includes("check") ? "checked"
    : lowerAction.includes("creat") ? "created"
    : lowerAction.includes("handover") ? "handover"
    : "edited";
};

const timestamp = (event) => {
  const time = new Date(event.created_at).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// Newest first; entries recorded in the same second keep the order they were written.
const newestFirst = (a, b) => (timestamp(b) - timestamp(a)) || (Number(b.id) || 0) - (Number(a.id) || 0);

export const buildPackHistoryEvents = (history = []) => (
  [...history]
    .sort(newestFirst)
    .map((event, index) => {
      const action = String(event.action || "Pack updated");
      const partChange = partChangeEvents[event.details?.event];
      const created = moment(event.created_at);
      const facts = partChange
        ? partChange.facts(event.details)
          .filter(([, value]) => value)
          .map(([label, value]) => ({ label, value: String(value) }))
        : [];

      return {
        id: String(event.id || event._id || index),
        type: partChange ? partChange.type : typeForAction(action),
        title: partChange ? partChange.title : action,
        detail: !partChange && event.additional_info && event.additional_info !== "N/A" ? event.additional_info : "",
        facts,
        actorLabel: partChange?.actorLabel || "by",
        user: event.user_name || "Worksite Manager",
        dateLabel: created.isValid() ? created.format("D MMMM YYYY") : "Date not recorded",
        time: created.isValid() ? created.format("HH:mm") : "",
      };
    })
);
